import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { closestCorners, DndContext, DragOverlay, pointerWithin, useDraggable, useDroppable, type CollisionDetection, type DragEndEvent, type DragOverEvent, type DragStartEvent } from '@dnd-kit/core'
import { Avatar, PairAvatars } from '@/components/Avatar'
import { eventTone, type EventTone } from '@/components/eventTone'
import { ChevronDownIcon, ChevronRightIcon, LockIcon, PlusIcon } from '@/components/icons'
import { StatusRing } from '@/components/StatusRing'
import { buzzOnPickUp, useDragSensors } from '@/hooks/useDragSensors'
import type { ShownEvent } from '@/hooks/useEvents'
import { usePeople, type People } from '@/hooks/usePeople'
import { useSwipe } from '@/hooks/useSwipe'
import { useTaskActions } from '@/hooks/useTaskActions'
import { addDays, fmtDate, startOfWeek, weekdayName } from '@/logic/dates'
import { eventsOnDay } from '@/logic/events'
import { dateLine, kindLabel } from '@/logic/labels'
import { itemsOnDay, openCount, trayGroups, type PlanItem } from '@/logic/plan'
import type { Now } from '@/logic/status'
import { useCategories } from '@/store/categories'
import { usePrefs } from '@/store/prefs'
import { setCalendarDate, setPlanWeek, useUi } from '@/store/ui'
import type { DateStr, Task } from '@/types'
import { HourGrid } from './HourGrid'
import { addFor, nextPeriod, plural, prevPeriod } from './shared'
import { useSheen } from './useSheen'
import './DayView.css'

/** Open rows the panel shows before "Show all". */
const FOLD = 5
const TONE_ORDER: EventTone[] = ['p1', 'p2', 'both']
const NO_ITEMS: PlanItem[] = []

/** A task in the hour grid: one that keeps a time on the day, still open and not a preview. */
const inGrid = (i: PlanItem) => !!i.time && !i.done && !i.faint

/** A pointer drops only on what is under it, so letting go over the grid changes nothing; the keyboard moves between targets. */
const collision: CollisionDetection = (args) => (args.pointerCoordinates ? pointerWithin(args) : closestCorners(args))

interface StripDay {
  date: DateStr
  open: number
  events: number
  tones: EventTone[]
}

interface DayViewProps {
  tasks: Task[]
  events: ShownEvent[]
  now: Now
  date: DateStr
  wide: boolean
}

/**
 * Day: a strip of the day's week, the day's tasks without a time in a panel,
 * then the hours. On a phone the panel starts folded to its counts and stays
 * open once opened; on a wide screen it is a column beside the hours. A long
 * press on a task lifts it, and dropping it on a day in the strip plans it
 * there.
 */
export function DayView({ tasks, events, now, date, wide }: DayViewProps) {
  const today = now.date
  const people = usePeople()
  const actions = useTaskActions()
  const tasksOpen = useUi((s) => s.dayTasksOpen)
  const hideDone = usePrefs((s) => !!s.filter.hideDone)
  const monday = startOfWeek(date)

  const byDay = useMemo(() => {
    const map = new Map<DateStr, PlanItem[]>()
    for (let i = 0; i < 7; i += 1) {
      const d = addDays(monday, i)
      map.set(d, itemsOnDay(tasks, d, today, hideDone, true))
    }
    return map
  }, [monday, tasks, today, hideDone])
  const strip = useMemo<StripDay[]>(
    () =>
      [...byDay.entries()].map(([d, items]) => {
        const onDay = eventsOnDay(events, d)
        const tones = new Set(onDay.map((e) => eventTone(e.owner, people)))
        return { date: d, open: openCount(items), events: onDay.length, tones: TONE_ORDER.filter((t) => tones.has(t)) }
      }),
    [byDay, events, people],
  )
  const items = byDay.get(date) ?? NO_ITEMS
  const timed = useMemo(() => items.filter(inGrid), [items])
  const listed = useMemo(() => items.filter((i) => !inGrid(i)), [items])
  const dayEvents = useMemo(() => eventsOnDay(events, date), [events, date])
  const thisMonday = startOfWeek(today)
  const notDone = useMemo(() => (date === today ? trayGroups(tasks, thisMonday, today).notdone.length : 0), [date, today, tasks, thisMonday])

  // ---- dragging a task to a day in the strip ----
  const sensors = useDragSensors()
  const [active, setActive] = useState<PlanItem | null>(null)
  const [over, setOver] = useState<string | null>(null)
  const dragging = useRef(false)
  const finish = () => {
    setActive(null)
    setOver(null)
    // the touch that ends a drag also ends a swipe; let that one pass first
    window.setTimeout(() => {
      dragging.current = false
    }, 0)
  }
  const onDragStart = (e: DragStartEvent) => {
    dragging.current = true
    buzzOnPickUp(e)
    setActive((e.active.data.current?.item as PlanItem | undefined) ?? null)
  }
  const onDragOver = (e: DragOverEvent) => setOver(e.over ? String(e.over.id) : null)
  const onDragEnd = (e: DragEndEvent) => {
    const item = e.active.data.current?.item as PlanItem | undefined
    const target = e.over?.data.current?.day as DateStr | undefined
    finish()
    if (!item || !target || target === date) return
    actions.planOn(item.task, target)
  }

  // a quick sideways swipe moves a day
  const swipeRef = useSwipe(nextPeriod, prevPeriod, { blocked: () => dragging.current })

  const link = notDone > 0 && <NotDoneLink count={notDone} monday={thisMonday} wide={wide} />
  // keyed by day, so "Show all" folds again on another day
  const panel = <TasksPanel key={date} date={date} items={listed} timedCount={timed.length} now={now} people={people} open={wide || tasksOpen} collapsible={!wide} over={over === 'panel'} />
  const grid = <HourGrid date={date} now={now} tasks={timed} events={dayEvents} people={people} />

  return (
    <DndContext sensors={sensors} collisionDetection={collision} onDragStart={onDragStart} onDragOver={onDragOver} onDragEnd={onDragEnd} onDragCancel={finish}>
      <div ref={swipeRef} className={`cday ${wide ? 'cday--wide' : ''}`}>
        <WeekStrip days={strip} date={date} today={today} over={over} />
        {wide ? (
          <div className="cday__main">
            <aside className="cday__side" aria-label="Tasks">
              {link}
              {panel}
            </aside>
            <div className="cday__hours">{grid}</div>
          </div>
        ) : (
          <>
            {link}
            {panel}
            {grid}
          </>
        )}
      </div>
      <DragOverlay dropAnimation={null}>{active ? <DragGhost item={active} /> : null}</DragOverlay>
    </DndContext>
  )
}

/** The selected day's week, Monday to Sunday. Each day is a drop target while a task is lifted. */
function WeekStrip({ days, date, today, over }: { days: StripDay[]; date: DateStr; today: DateStr; over: string | null }) {
  return (
    <div className="cstrip" role="group" aria-label="Days of the week">
      {days.map((d) => (
        <StripButton key={d.date} day={d} on={d.date === date} isToday={d.date === today} over={over === `strip:${d.date}`} />
      ))}
    </div>
  )
}

function StripButton({ day, on, isToday, over }: { day: StripDay; on: boolean; isToday: boolean; over: boolean }) {
  const { setNodeRef } = useDroppable({ id: `strip:${day.date}`, data: { day: day.date } })
  const label = [fmtDate(day.date), isToday ? 'today' : null, day.open ? plural(day.open, 'task') : null, day.events ? plural(day.events, 'event') : null].filter(Boolean).join(', ')
  return (
    <button
      ref={setNodeRef}
      type="button"
      className={`cstrip__day ${isToday ? 'cstrip__day--today' : ''} ${on ? 'cstrip__day--on' : ''} ${over ? 'cstrip__day--over' : ''}`}
      aria-pressed={on}
      aria-label={label}
      onClick={() => setCalendarDate(day.date)}
    >
      <span className="cstrip__wd">{weekdayName(day.date)[0]}</span>
      <span className="cstrip__n">{Number(day.date.slice(8))}</span>
      <span className="cstrip__dots">
        {day.open > 0 && <span className="cstrip__dot cstrip__dot--task" />}
        {day.tones.map((t) => (
          <span key={t} className={`cstrip__dot ev--${t}`} />
        ))}
      </span>
    </button>
  )
}

/** Today only: what the Plan tab's Not done group holds, with the way there. */
function NotDoneLink({ count, monday, wide }: { count: number; monday: DateStr; wide: boolean }) {
  const navigate = useNavigate()
  const open = () => {
    const prefs = usePrefs.getState()
    setPlanWeek(monday)
    prefs.openGroup('notdone', !wide && prefs.layoutPhone === 'columns')
    if (wide) prefs.setTrayColumnOpen(true)
    navigate('/plan')
  }
  return (
    <button type="button" className="cday__notdone" onClick={open}>
      <span>
        {count} not done this week · Open in Plan
      </span>
      <ChevronRightIcon size={16} weight={2.4} />
    </button>
  )
}

interface TasksPanelProps {
  date: DateStr
  /** The day's rows that are not in the hour grid: tasks without a time, previews and done rows. */
  items: PlanItem[]
  timedCount: number
  now: Now
  people: People
  open: boolean
  collapsible: boolean
  over: boolean
}

/**
 * The day's tasks without a time. Folded, only the counts show: "Tasks · 2 ·
 * 1 done". Open, one line per task, done ones struck at the bottom; past five
 * open it folds to five and "Show all". A task dropped on it lands on this day.
 */
function TasksPanel({ date, items, timedCount, now, people, open, collapsible, over }: TasksPanelProps) {
  const [all, setAll] = useState(false)
  const { setNodeRef } = useDroppable({ id: 'panel', data: { day: date } })
  const openN = items.filter((i) => !i.done && !i.faint && !i.dueMark).length
  const doneN = items.filter((i) => i.done).length
  const folded = !all && openN > FOLD
  const shown = folded ? items.slice(0, FOLD) : items
  const head = (
    <>
      <span className="eyebrow">Tasks</span>
      <span className="cpanel__counts">
        · {openN}
        {doneN > 0 && ` · ${doneN} done`}
      </span>
    </>
  )
  return (
    <section ref={setNodeRef} className={`cpanel ${open ? 'cpanel--open' : ''} ${over ? 'cpanel--over' : ''}`} aria-label={`Tasks for ${fmtDate(date)}`}>
      <div className="cpanel__head">
        {collapsible ? (
          <button type="button" className="cpanel__toggle" aria-expanded={open} onClick={() => useUi.setState({ dayTasksOpen: !open })}>
            {open ? <ChevronDownIcon size={15} weight={2.4} /> : <ChevronRightIcon size={15} weight={2.4} />}
            {head}
          </button>
        ) : (
          <h2 className="cpanel__toggle">{head}</h2>
        )}
        {open && (
          <button type="button" className="icon-btn cpanel__add" aria-label={`Add a task for ${fmtDate(date)}`} onClick={() => addFor(date)}>
            <PlusIcon size={18} weight={2.2} />
          </button>
        )}
      </div>
      {open &&
        (items.length ? (
          <div className="cpanel__list">
            {shown.map((item) => (
              <PanelRow key={item.key} item={item} date={date} now={now} people={people} />
            ))}
            {folded && (
              <button type="button" className="cpanel__more" onClick={() => setAll(true)}>
                Show all {items.length}
              </button>
            )}
          </div>
        ) : (
          <p className="cpanel__empty">{timedCount ? 'Nothing planned without a time' : 'Nothing planned'}</p>
        ))}
    </section>
  )
}

/** Whose a task is: the assignee's letter, both for either of us, or a lock when private. */
function Who({ task, people }: { task: Task; people: People }) {
  if (task.scope === 'private') return <LockIcon size={15} title="Private" className="crow__lock" />
  const uid = task.assigneeUid ?? null
  return uid ? <Avatar uid={uid} people={people} small label={`For ${people.names[uid] ?? 'someone'}`} /> : <PairAvatars people={people} small />
}

/**
 * One line per task: whose it is, the title, then on the right its due date when
 * that is another day (or "Routine", the repeat, "Waiting"), and the ring.
 * The row opens the task, a preview read-only; a long press lifts it.
 */
function PanelRow({ item, date, now, people }: { item: PlanItem; date: DateStr; now: Now; people: People }) {
  const navigate = useNavigate()
  const actions = useTaskActions()
  const category = useCategories((s) => (item.task.categoryId ? (s.byId[item.task.categoryId] ?? null) : null))
  const t = item.task
  const color = category ? `var(--${category.color})` : null
  const movable = !item.done && !item.faint && !item.dueMark
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({ id: item.key, data: { item }, disabled: !movable })
  const ref = useRef<HTMLDivElement | null>(null)
  const setRefs = useCallback(
    (el: HTMLDivElement | null) => {
      ref.current = el
      setNodeRef(el)
    },
    [setNodeRef],
  )
  useSheen(ref, t.id)
  const openTask = () => navigate(`/task/${t.id}`, { state: item.faint ? { readOnly: true } : undefined })

  if (item.done) {
    return (
      <div ref={setRefs} className="crow crow--done" data-task-id={t.id}>
        <button type="button" className="crow__open" aria-label={`Open ${t.title}`} onClick={openTask} />
        <span className="crow__title">{t.title}</span>
        <StatusRing state="done" color={color} label={`Reopen ${t.title}`} onToggle={() => actions.toggleDone(t, item.completion)} />
      </div>
    )
  }

  const line = item.faint ? null : dateLine(t, { day: date }, now)
  const kind = kindLabel(t)
  const info = line ?? (t.waiting && !item.faint ? { text: 'Waiting', tone: null } : kind ? { text: kind, tone: null } : null)
  return (
    <div
      ref={setRefs}
      className={`crow ${item.faint ? 'crow--faint' : ''} ${movable ? 'crow--drag' : ''} ${isDragging ? 'crow--lifted' : ''}`}
      data-task-id={t.id}
      onContextMenu={movable ? (e) => e.preventDefault() : undefined}
      {...(movable ? listeners : {})}
    >
      <button type="button" className="crow__open" aria-label={`Open ${t.title}`} onClick={openTask} />
      <span className="crow__who">
        <Who task={t} people={people} />
      </span>
      <span className="crow__title">{t.title}</span>
      {info && <span className={`crow__info ${info.tone ? `crow__info--${info.tone}` : ''}`}>{info.text}</span>}
      <StatusRing
        state={t.waiting ? 'waiting' : 'open'}
        color={color}
        label={item.faint ? `Not yet: ${t.title}` : `Complete ${t.title}`}
        inert={item.faint}
        onToggle={() => actions.toggleDone(t)}
      />
      {/* the whole row drags by touch and mouse; this unseen button is the keyboard's way in */}
      {movable && <button type="button" ref={setActivatorNodeRef} className="crow__handle" aria-label={`Move ${t.title} to another day`} {...attributes} />}
    </div>
  )
}

/** The copy that follows the finger or mouse while dragging. */
export function DragGhost({ item }: { item: PlanItem }) {
  const color = useCategories((s) => (item.task.categoryId && s.byId[item.task.categoryId] ? `var(--${s.byId[item.task.categoryId].color})` : null))
  return (
    <div className="cghost">
      <StatusRing state={item.task.waiting ? 'waiting' : 'open'} color={color} size="small" label="" inert />
      <span className="cghost__title">{item.task.title}</span>
    </div>
  )
}
